import { createTrackingId } from "./userProductInterest";

export const PROPERTY_APPLICATION_STEPS = {
  0: "Property valuation", 0.5: "Supporting documents", 1: "Loan details",
  2: "CBS report", 3: "Review and submit",
};

// Keep user inputs and document references, not binary files or derived schedules.
export function applicationSnapshot(form, details = {}) {
  const { loanRepaymentSchedule, ...inputs } = form;
  return JSON.parse(JSON.stringify({ form: inputs, ...details }, (key, value) => {
    if (typeof File !== "undefined" && value instanceof File) {
      return { name: value.name, size: value.size, type: value.type };
    }
    return value;
  }));
}

export function createProgressRecorder({ userId, storage, send, onStatus = () => {} }) {
  const prefix = `property-progress:${userId}:`;
  const sessionKey = `property-progress-active:${userId}`;
  let active;
  try { active = JSON.parse(storage.getItem(sessionKey)); } catch {}
  let sessionId = active?.session_id || createTrackingId();
  let revision = active?.revision || 0;
  let latest = null;
  let lastSnapshot = null;
  let inFlight = false;
  let generation = 0;
  const read = (key) => {
    try { return JSON.parse(storage.getItem(key)); } catch { return null; }
  };
  const remove = (key) => { try { storage.removeItem(key); } catch {} };
  const pending = () => {
    const entries = new Map();
    try {
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith(prefix)) {
          const value = read(key);
          if (value) entries.set(key, value);
        }
      }
    } catch {}
    if (latest) entries.set(prefix + latest.session_id, latest);
    return entries;
  };
  return {
    get sessionId() { return sessionId; },
    capture(snapshot, step) {
      const serialized = JSON.stringify({ snapshot, step });
      if (serialized === lastSnapshot) return;
      lastSnapshot = serialized;
      latest = { session_id: sessionId, revision: ++revision, step, snapshot };
      try {
        storage.setItem(sessionKey, JSON.stringify({ session_id: sessionId, revision }));
        storage.setItem(prefix + sessionId, JSON.stringify(latest));
      } catch {}
      onStatus("saving");
    },
    async flush(keepalive = false) {
      if (inFlight && !keepalive) return;
      if (!keepalive) inFlight = true;
      const startedGeneration = generation;
      try {
        for (const [key, value] of pending()) {
          if (startedGeneration !== generation) return;
          const response = await send(value, keepalive);
          if (startedGeneration !== generation) return;
          if (response?.submitted && value.session_id === sessionId) {
            this.complete();
            return;
          }
          if (read(key)?.revision === value.revision) remove(key);
          if (latest?.session_id === value.session_id && latest.revision === value.revision) {
            latest = null;
            onStatus("saved");
          }
        }
      } catch {
        if (startedGeneration === generation) onStatus("retrying");
      } finally {
        if (!keepalive) inFlight = false;
      }
    },
    complete() {
      generation += 1;
      remove(sessionKey);
      for (const key of pending().keys()) remove(key);
      latest = null;
      lastSnapshot = null;
      revision = 0;
      sessionId = createTrackingId();
      onStatus("idle");
    },
  };
}
